import { z } from 'zod';

import countWords from 'src/utils/count-words';

export const ACTIVITY_ANSWER_MAX_WORDS = 250;

export const privacyDocumentSchema = z.object({
  originalName: z.string(),
  downloadUrl: z.string(),
});

const introductionFieldsSchema = z.object({
  contractLeadName: z.string().min(1, 'Contract lead name is required'),
  contractLeadEmail: z
    .string()
    .min(1, 'Contract lead email is required')
    .email('Please enter a valid email address'),
  socialValueLeadName: z.string().min(1, 'Social value lead name is required'),
  socialValueLeadEmail: z
    .string()
    .min(1, 'Social value lead email is required')
    .email('Please enter a valid email address'),
  privacyPolicyRequired: z.boolean().optional(),
  privacyPolicyAccepted: z.boolean().optional(),
});

export const introductionSchema = introductionFieldsSchema.superRefine((data, ctx) => {
  if (data.privacyPolicyRequired && !data.privacyPolicyAccepted) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['privacyPolicyAccepted'],
      message: 'You must accept the Procurer Privacy Policy before continuing',
    });
  }
});

// Activities step schema
export const activitiesSchema = z.object({
  commitToActivities: z
    .array(
      z
        .object({
          id: z.number(),
          title: z.string(),
          units: z
            .number()
            .int('Units must be a whole number')
            .min(0, 'Units must be non-negative'),
          minimumUnits: z.number().nullish(),
          maximumUnits: z.number().nullish(),
          additionalQuestions: z
            .array(
              z.object({
                text: z.string(),
                answer: z
                  .string()
                  .min(1, 'This question is required')
                  .refine((value) => countWords(value) <= ACTIVITY_ANSWER_MAX_WORDS, {
                    message: `Answer exceeds maximum word count of ${ACTIVITY_ANSWER_MAX_WORDS}`,
                  }),
              })
            )
            .optional(),
        })
        .refine(
          (data) => {
            // Check if units is within min/max range (only when min/max are numbers)
            if (typeof data.minimumUnits === 'number' && data.units < data.minimumUnits) {
              return false;
            }
            if (typeof data.maximumUnits === 'number' && data.units > data.maximumUnits) {
              return false;
            }
            return true;
          },
          (data) => {
            // Provide specific error message based on which constraint was violated
            const isBelowMin =
              typeof data.minimumUnits === 'number' && data.units < data.minimumUnits;
            const isAboveMax =
              typeof data.maximumUnits === 'number' && data.units > data.maximumUnits;

            if (isAboveMax) {
              return {
                message: `Units must not exceed ${data.maximumUnits}`,
                path: ['units'],
              };
            }

            if (isBelowMin) {
              return {
                message: `Units must be at least ${data.minimumUnits}`,
                path: ['units'],
              };
            }

            return {
              message: 'Invalid units',
              path: ['units'],
            };
          }
        )
    )
    .min(1, 'Please select at least one activity'),
});

// Qualitative Questions step schema
export const qualitativeQuestionsSchema = z.object({
  submissionQuestionsAnswers: z
    .array(
      z
        .object({
          // id: z.number(),
          // title: z.string(),
          answer: z.string().min(1, 'This question is required'),
          // maxScore: z.number().optional(),
          answerMaxWordCount: z.number().optional(),
        })
        .refine(
          (data) => {
            if (!data.answerMaxWordCount) return true;
            const wordCount = countWords(data.answer);
            return wordCount <= data.answerMaxWordCount;
          },
          (data) => ({
            message: `Answer exceeds maximum word count of ${data.answerMaxWordCount}`,
            path: ['answer'],
          })
        )
    )
    .min(1, 'Please answer all questions'),
});

// Complete application state schema (includes contract data and metadata)
export const completeApplicationStateSchema = z.object({
  // Form data
  contractLeadName: z.string().optional(),
  contractLeadEmail: z.string().optional(),
  socialValueLeadName: z.string().optional(),
  socialValueLeadEmail: z.string().optional(),
  commitToActivities: z.array(z.any()).optional(),
  submissionQuestionsAnswers: z.array(z.any()).optional(),

  privacyPolicyAccepted: z.boolean().optional(),
  privacyPolicyRequired: z.boolean().optional(),
  privacyPolicy: privacyDocumentSchema.nullable().optional(),

  // Contract data (read-only)
  contract: z.any().optional(),
  procurerInterestedActivities: z.array(z.any()).optional(),

  // Draft / change-request state
  hasDraft: z.boolean().optional(),
  changeRequests: z.array(z.any()).optional(),

  // Error handling
  errors: z.record(z.any()).optional(),
  generalError: z.string().optional(),
});

// Combined schema for the entire application
export const supplierContractsApplicationsSchema = introductionFieldsSchema
  .merge(activitiesSchema)
  .merge(qualitativeQuestionsSchema);

// Step-specific schema mapping
export const stepSchemas = {
  introduction: introductionSchema,
  activities: activitiesSchema,
  'qualitative-questions': qualitativeQuestionsSchema,
} as const;

export type StepName = keyof typeof stepSchemas;
export type CompleteApplicationState = z.infer<typeof completeApplicationStateSchema>;
export type SupplierContractsApplications = z.infer<typeof supplierContractsApplicationsSchema>;
export type IntroductionData = z.infer<typeof introductionSchema>;
export type ActivitiesData = z.infer<typeof activitiesSchema>;
export type QualitativeQuestionsData = z.infer<typeof qualitativeQuestionsSchema>;

export const validateSupplierContractApplication = (data: any, step: string) => {
  const stepName = step as StepName;
  if (stepName in stepSchemas) {
    return stepSchemas[stepName].parse(data);
  }
  return supplierContractsApplicationsSchema.parse(data);
};
